import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Crown, Flame, Scroll, Check, Infinity as InfinityIcon, Heart } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSeoTitle } from "@/lib/useSeoTitle";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ICONS = {
  apprentice: Flame,
  mage: Scroll,
  archmage: Crown,
  nsfw_addon: Heart,
};

const FEATURES = {
  apprentice: [
    "Claude Sonnet 4.5 & Haiku 4.5 narrators",
    "350 turns / month",
    "No daily cap",
    "Cancel anytime",
  ],
  mage: [
    "GPT-5 & Gemini 2.5 Pro narrators",
    "Every Apprentice narrator",
    "700 turns / month",
    "Patron badge on your hero card",
  ],
  archmage: [
    "Every Mage narrator",
    "1,800 turns / month",
    "Your name in the patron scroll",
    "Priority on new realms & features",
  ],
  nsfw_addon: [
    "Detailed consensual scenes — all orientations",
    "Cydonia, Magnum, Euryale & Lunaris narrators",
    "Graphic violence",
    "Stacks on any tier · Adults 18+ only",
  ],
};

export default function Tip() {
  const nav = useNavigate();
  const { user } = useAuth();
  useSeoTitle({
    title: "Pricing — TalesTalesTales AI Novel Subscriptions",
    description: "Wanderer free (10 turns/day), Apprentice $9.99 (350 turns/mo), Mage $19.99 (700), Archmage $39.99 (1,800). Optional Mature Realms 18+ add-on. Cancel anytime.",
    path: "/pricing",
  });
  const [packages, setPackages] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const [trialBusy, setTrialBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then(r => setPackages(r.data)).catch(() => {});
  }, []);

  /**
   * Start the 2-day free Mage trial. Routes to the backend `/payments/
   * trial-start` endpoint which creates a Stripe Checkout Session in
   * subscription mode with `trial_period_days=2` and forces card
   * collection up front. After the trial expires the customer is
   * auto-charged the Mage tier monthly price unless they cancel.
   */
  const startTrial = async () => {
    if (!user) { nav("/login"); return; }
    if (user.trial_used) {
      toast.error("You've already used your free trial. Subscribe directly to keep playing on Mage.");
      return;
    }
    setTrialBusy(true);
    try {
      const r = await api.post("/payments/trial-start", { origin_url: window.location.origin });
      const url = r.data.url;
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch {
        window.open(url, "_blank", "noopener,noreferrer");
        setTrialBusy(false);
        return;
      }
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The trial could not be started.");
      setTrialBusy(false);
    }
  };

  const purchase = async (packageId) => {
    if (!user) { nav("/login"); return; }
    setBusyId(packageId);
    try {
      const r = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      const url = r.data.url;
      // Stripe Checkout refuses to render inside iframes (Emergent App Preview
      // is iframed). Break out of any iframe by targeting the top window;
      // fall back to a new tab if cross-origin blocks the top redirect.
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch {
        // Cross-origin frame — can't navigate top. Use a new tab.
        window.open(url, "_blank", "noopener,noreferrer");
        setBusyId(null);
        return;
      }
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The vault refused.");
      setBusyId(null);
    }
  };

  const tierPackages = packages.filter(p => p.id !== "nsfw_addon");
  const addonPackages = packages.filter(p => p.id === "nsfw_addon");

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <div id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="rise mb-10 text-center">
          <div className="font-ui text-xs tracking-[0.35em] text-amber-400/80 uppercase mb-3">Choose your patronage</div>
          <h1 className="font-display text-5xl text-amber-50">Subscriptions</h1>
          <p className="font-body text-amber-100/65 mt-4 max-w-2xl mx-auto leading-relaxed">
            Every wanderer gets <span className="gold-text font-display">10 turns/day</span> free.
            Patrons unlock deeper realms, premier narrators, and many more turns.
          </p>
          {user?.tier && user.tier !== "free" && (
            <div data-testid="current-tier-badge" className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/50 bg-amber-900/20">
              <Crown className="w-4 h-4 text-amber-300" />
              <span className="font-display text-amber-100">
                Current: <span className="gold-text">{user.tier_label || user.tier}</span>
              </span>
            </div>
          )}
        </div>

        {/* Tier cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <FreeTierCard user={user} />
          {tierPackages.map((p, i) => {
            const Icon = ICONS[p.id] || Flame;
            const recommended = p.id === "mage";
            const features = FEATURES[p.id] || [];
            return (
              <div
                key={p.id}
                data-testid={`tier-card-${p.id}`}
                className={`parchment gold-border rounded-xl p-7 relative transition rise hover:candle-glow flex flex-col ${
                  recommended ? "candle-glow border-amber-500/60 md:scale-[1.03]" : ""
                }`}
                style={{ animationDelay: `${(i + 1) * 100}ms` }}
              >
                {recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-amber-600 text-stone-900 font-ui text-[10px] uppercase tracking-widest">
                    Patron&apos;s Choice
                  </div>
                )}
                <Icon className="w-7 h-7 text-amber-400 mb-3 flicker" />
                <h3 className="font-display text-2xl text-amber-50">{p.label}</h3>
                <div className="font-display text-4xl text-amber-200 mt-3">
                  ${p.amount.toFixed(2)}
                  <span className="font-body text-sm text-amber-200/50 ml-1">/ month</span>
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 font-body text-sm text-amber-100/85">
                      <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                  {/* Same dim "Explicit scenes require the add-on" line that
                      sits on the Wanderer card — surfaces on Apprentice and
                      Mage too so subscribers know the add-on is still needed
                      for explicit content. Archmage omits this on purpose to
                      keep the top tier feeling unrestricted in copy. */}
                  {(p.id === "apprentice" || p.id === "mage") && (
                    <li className="flex items-start gap-2 font-body text-sm text-amber-100/85 opacity-60">
                      <InfinityIcon className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                      Explicit scenes require the add-on
                    </li>
                  )}
                </ul>
                <Button
                  data-testid={`tier-buy-${p.id}`}
                  disabled={busyId === p.id || (user?.tier === p.id)}
                  onClick={() => purchase(p.id)}
                  className="w-full mt-6 bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-11"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {user?.tier === p.id ? "You're here" : busyId === p.id ? "Opening..." : "Subscribe"}
                </Button>
                {/* 2-day free trial — only on Mage, only for users who
                    haven't used a trial yet AND aren't already on a paid
                    tier. Card collected up-front; auto-charges $X.XX
                    after 48h unless cancelled. */}
                {p.id === "mage" && !user && (
                  <Button
                    data-testid="tier-login-trial-mage"
                    onClick={() => nav("/login?from=trial")}
                    variant="outline"
                    className="w-full mt-2 border-amber-500/50 text-amber-200 hover:bg-amber-900/25 font-ui h-10"
                  >
                    Log in to start 2-day Mage trial
                  </Button>
                )}
                {p.id === "mage" && user && !user.trial_used && (user.tier || "free") === "free" && (
                  <Button
                    data-testid="tier-trial-mage"
                    onClick={startTrial}
                    disabled={trialBusy}
                    variant="outline"
                    className="w-full mt-2 border-amber-500/50 text-amber-200 hover:bg-amber-900/25 font-ui h-10"
                  >
                    {trialBusy ? "Opening..." : "Try free for 2 days"}
                  </Button>
                )}
                {p.id === "mage" && user?.trial_used && (user.tier || "free") === "free" && (
                  <p className="text-[11px] font-body text-amber-200/55 mt-2 italic text-center">
                    Trial already used — subscribe to continue.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* NSFW Add-on */}
        {addonPackages.length > 0 && (
          <div className="mt-12">
            <div className="text-center mb-6">
              <div className="font-ui text-xs tracking-[0.35em] text-amber-400/80 uppercase mb-2">Optional add-on</div>
              <h2 className="font-display text-3xl text-amber-50">Mature Realms Unlock</h2>
              <p className="font-body text-amber-100/65 mt-2 max-w-xl mx-auto italic">
                Consensual romance and tender intimacy are already part of every saga — Mature Realms unlocks the explicit scenes.
              </p>
            </div>

            {/* Explicit content policy */}
            <div
              data-testid="mature-realms-policy"
              className="max-w-2xl mx-auto mb-6 rounded-lg border border-rose-700/40 bg-rose-950/15 p-5"
            >
              <div className="font-ui text-[10px] tracking-[0.3em] text-rose-300/80 uppercase mb-2">
                What Mature Realms unlocks
              </div>
              <p className="font-body text-sm text-amber-100/90 leading-relaxed">
                Detailed adult scenes across every orientation, gender identity and pairing.
                Graphic violence permitted.
              </p>
              <p className="font-body text-sm text-rose-200/90 leading-relaxed mt-3">
                Strictly <span className="font-display">no minors</span>, <span className="font-display">no real persons</span>,
                and <span className="font-display">no non-consensual sexual content</span>. For adults 18+ only.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <div className="hidden md:block" />
              {addonPackages.map((p) => {
                const Icon = ICONS[p.id];
                const owned = user?.nsfw_addon_active;
                return (
                  <div
                    key={p.id}
                    data-testid={`tier-card-${p.id}`}
                    className="parchment gold-border rounded-xl p-7 flex flex-col border-rose-700/40"
                  >
                    <Icon className="w-7 h-7 text-rose-400 mb-3 flicker" />
                    <h3 className="font-display text-2xl text-amber-50">{p.label}</h3>
                    <div className="font-display text-4xl text-amber-200 mt-3">
                      ${p.amount.toFixed(2)}
                      <span className="font-body text-sm text-amber-200/50 ml-1">/ month</span>
                    </div>
                    <ul className="mt-5 space-y-2 flex-1">
                      {FEATURES.nsfw_addon.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 font-body text-sm text-amber-100/85">
                          <Check className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      data-testid={`tier-buy-${p.id}`}
                      disabled={busyId === p.id || owned}
                      onClick={() => purchase(p.id)}
                      className="w-full mt-6 bg-rose-700 hover:bg-rose-600 text-amber-50 font-ui h-11"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      {owned ? "Active" : busyId === p.id ? "Opening..." : "Unlock"}
                    </Button>
                  </div>
                );
              })}
              <div className="hidden md:block" />
            </div>
          </div>
        )}

        <p className="font-body text-amber-200/40 text-xs text-center mt-10 italic">
          Recurring monthly subscription via Stripe. Cancel anytime — manage your subscription from your
          Profile or via the Stripe billing portal.
        </p>
      </div>
      <Footer />
    </div>
  );
}

function FreeTierCard({ user }) {
  const isHere = !user?.tier || user.tier === "free";
  return (
    <div
      data-testid="tier-card-free"
      className="parchment gold-border rounded-xl p-7 relative rise flex flex-col"
    >
      <Flame className="w-7 h-7 text-amber-400/70 mb-3" />
      <h3 className="font-display text-2xl text-amber-50">Wanderer</h3>
      <div className="font-display text-4xl text-amber-200/90 mt-3">
        Free
      </div>
      <ul className="mt-5 space-y-2 flex-1 font-body text-sm text-amber-100/85">
        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />Storyteller (Llama 3.3 70B) narration</li>
        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />10 turns / day</li>
        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />Tender romance & courtship welcomed</li>
        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />Forge heroes, explore realms, save sagas</li>
        <li className="flex items-start gap-2 opacity-60"><InfinityIcon className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />Explicit scenes require the add-on</li>
      </ul>
      <Button
        disabled
        className="w-full mt-6 bg-amber-900/30 text-amber-300 font-ui h-11 cursor-default"
      >
        {isHere ? "Your tier" : "—"}
      </Button>
    </div>
  );
}
